/**
 * Embedded Control Bar breakpoints, measured on the iframe width (Figma `External Embed Sizing
 * Specs`). The CSS side is the `--container-embed-*` theme tokens in `app/[locale]/globals.css`;
 * `tests/constants/embed-breakpoints.test.ts` keeps both in sync.
 */
export const EMBED_BREAKPOINTS = { xs: 250, sm: 320, md: 481, lg: 769 } as const

export type DesktopControlBarVariant = 'default' | 'viverse-pro'
export type DesktopControlBarSurface = 'normal' | 'fullscreen'

export type ControlBarPresentation =
  | { kind: 'none' }
  | { kind: 'mobile' }
  | { kind: 'kids-mode' }
  | {
      kind: 'desktop'
      surface: DesktopControlBarSurface
      variant: DesktopControlBarVariant
    }
  | EmbeddedControlBarPresentation

/**
 * Embedded normal view uses the width-tiered bottom bar; embedded native fullscreen reuses the
 * Desktop or Mobile layout, chosen by `device`.
 */
export type EmbeddedControlBarPresentation =
  | { kind: 'embedded'; surface: 'normal'; variant: DesktopControlBarVariant }
  | {
      kind: 'embedded'
      surface: 'fullscreen'
      device: 'desktop' | 'mobile'
      variant: DesktopControlBarVariant
    }

export type ControlBarPresentationOptions = {
  hasControlBarQueryParam: boolean
  hasCheckedEmbeddedFrame: boolean
  hasKidsModeQueryParam: boolean
  isEmbedded: boolean
  isFull3D: boolean
  isFullscreen: boolean
  isMobileApp: boolean
  isMobile: boolean
  isPreferredDevice: boolean
  isPremiumContent: boolean
  isStandaloneContent: boolean
  isVideoContent: boolean
  isViverseProOwner: boolean
}

export const resolveControlBarPresentation = ({
  hasCheckedEmbeddedFrame,
  hasControlBarQueryParam,
  hasKidsModeQueryParam,
  isEmbedded,
  isFull3D,
  isFullscreen,
  isMobileApp,
  isMobile,
  isPreferredDevice,
  isPremiumContent,
  isStandaloneContent,
  isVideoContent,
  isViverseProOwner,
}: ControlBarPresentationOptions): ControlBarPresentation => {
  // Conditions are evaluated top to bottom; the first match determines the presentation.
  if (!isStandaloneContent || !isPreferredDevice || isPremiumContent || isVideoContent) {
    return { kind: 'none' }
  }

  // Embedded pages are always `full3d`, so they must not fall through to the Desktop or Mobile
  // rules below. The Control Bar is opt-in through the `control_bar` query parameter.
  if (isEmbedded) {
    if (!hasControlBarQueryParam) return { kind: 'none' }

    const variant = isViverseProOwner ? 'viverse-pro' : 'default'

    if (!isFullscreen) return { kind: 'embedded', surface: 'normal', variant }

    return {
      kind: 'embedded',
      surface: 'fullscreen',
      device: isMobile ? 'mobile' : 'desktop',
      variant,
    }
  }

  if (isMobileApp && isFull3D && hasKidsModeQueryParam) {
    return { kind: 'kids-mode' }
  }

  if (isMobile) {
    return isFull3D ? { kind: 'mobile' } : { kind: 'none' }
  }

  // The server may not have identified an iframe request (no `sec-fetch-dest: iframe`), so a
  // Desktop `full3d` page waits for the client frame check before it can show the Desktop bar.
  if (isFull3D && !hasCheckedEmbeddedFrame) return { kind: 'none' }

  return {
    kind: 'desktop',
    surface: isFullscreen ? 'fullscreen' : 'normal',
    variant: isViverseProOwner ? 'viverse-pro' : 'default',
  }
}
